import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { suppliesApi } from '../../../services/api'
import TableActionButton from '../../../components/TableActionButton'
import SortableTh from '../../../components/SortableTh'
import { useSortableData } from '../../../hooks/useSortableData'

interface Supply {
  id: number
  name: string
  current_cost_per_kg: number
  is_active: boolean
  updated_at: string
}

interface SupplyForm {
  name: string
  current_cost_per_kg: string
}

const emptyForm: SupplyForm = { name: '', current_cost_per_kg: '' }

export default function SuppliesTab() {
  const [supplies, setSupplies] = useState<Supply[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<SupplyForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const fetchSupplies = async () => {
    try {
      setLoadError('')
      const res = await suppliesApi.list()
      setSupplies(res.data.supplies || [])
    } catch (err) {
      console.error('Error fetching supplies:', err)
      setLoadError('No se pudieron cargar los insumos. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSupplies()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (s: Supply) => {
    setForm({ name: s.name, current_cost_per_kg: String(s.current_cost_per_kg) })
    setEditingId(s.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    if (form.current_cost_per_kg === '' || parseFloat(form.current_cost_per_kg) < 0) {
      setError('Ingresa un costo por kg válido')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = { name: form.name.trim(), current_cost_per_kg: parseFloat(form.current_cost_per_kg) }
      if (editingId) {
        await suppliesApi.update(editingId, payload)
        toast.success('Insumo actualizado')
      } else {
        await suppliesApi.create(payload)
        toast.success('Insumo creado')
      }
      setShowForm(false)
      fetchSupplies()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar el insumo')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (s: Supply) => {
    setTogglingId(s.id)
    try {
      await suppliesApi.update(s.id, { is_active: !s.is_active })
      toast.success(s.is_active ? 'Insumo desactivado' : 'Insumo activado')
      fetchSupplies()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al actualizar el insumo')
    } finally {
      setTogglingId(null)
    }
  }

  const { sortedData: sortedSupplies, sortConfig, requestSort } = useSortableData(supplies)

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  const zeroCostCount = supplies.filter((s) => s.is_active && Number(s.current_cost_per_kg) === 0).length

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-espresso-500 max-w-2xl">
          Insumos usados en mezclado y empaquetado (Trigo, Azúcar, Etiqueta, Bolsa, Tostado ya existen). Evita crear
          duplicados — en su lugar, edita el costo por kg del insumo existente.
        </p>
        <button
          onClick={openNew}
          className="shrink-0 ml-4 px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nuevo Insumo
        </button>
      </div>

      {zeroCostCount > 0 && (
        <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {zeroCostCount} insumo{zeroCostCount > 1 ? 's' : ''} activo{zeroCostCount > 1 ? 's' : ''} con costo $0.00/kg.
          Establece su costo real antes de usarlo en un lote de producción.
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && supplies.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay insumos registrados aún.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Nombre" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo / kg" sortKey="current_cost_per_kg" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Actualizado" sortKey="updated_at" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-6 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedSupplies.map((s) => (
                  <tr key={s.id} className="hover:bg-cream-100">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{s.name}</td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                        Number(s.current_cost_per_kg) === 0 ? 'text-amber-700' : 'text-green-700'
                      }`}
                    >
                      ${Number(s.current_cost_per_kg).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-500">
                      {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          s.is_active ? 'bg-green-100 text-green-800' : 'bg-cream-200 text-espresso-700'
                        }`}
                      >
                        {s.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(s)} variant="primary">
                          Editar
                        </TableActionButton>
                        <TableActionButton
                          onClick={() => handleToggleActive(s)}
                          variant={s.is_active ? 'danger' : 'success'}
                          disabled={togglingId === s.id}
                        >
                          {togglingId === s.id ? 'Guardando...' : s.is_active ? 'Desactivar' : 'Activar'}
                        </TableActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Insumo' : 'Nuevo Insumo'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="supply-name" className="block text-sm font-medium text-espresso-700 mb-1">
                  Nombre *
                </label>
                <input
                  id="supply-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="supply-cost" className="block text-sm font-medium text-espresso-700 mb-1">
                  Costo por kg *
                </label>
                <input
                  id="supply-cost"
                  type="number"
                  name="current_cost_per_kg"
                  value={form.current_cost_per_kg}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
